import type { TaskType } from '@econtractor/types';
import { arrayMove } from '@dnd-kit/sortable';

export type KanbanColumnOrder = Record<string, number[]>;

const storageKey = (userId: number): string => `econtractor:kanbanOrder:${userId}`;

export const loadOrder = (userId: number): KanbanColumnOrder => {
    if (!userId || typeof window === 'undefined') return {};
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return {};
        const parsed = JSON.parse(raw) as KanbanColumnOrder;
        if (!parsed || typeof parsed !== 'object') return {};
        const result: KanbanColumnOrder = {};
        for (const [status, ids] of Object.entries(parsed)) {
            if (!Array.isArray(ids)) continue;
            result[status] = ids.map(Number).filter((id) => Number.isFinite(id) && id > 0);
        }
        return result;
    } catch {
        return {};
    }
};

export const saveOrder = (userId: number, order: KanbanColumnOrder): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(storageKey(userId), JSON.stringify(order));
    } catch {
        /* ignore quota / private mode */
    }
};

export const applyOrder = (
    tasks: TaskType[],
    status: number,
    order: KanbanColumnOrder,
    fallbackSort: (list: TaskType[]) => TaskType[],
): TaskType[] => {
    const saved = order[String(status)];
    if (!saved || saved.length === 0) {
        return fallbackSort(tasks);
    }

    const byId = new Map(tasks.map((task) => [task.ID, task]));
    const ordered: TaskType[] = [];
    for (const id of saved) {
        const task = byId.get(id);
        if (task) {
            ordered.push(task);
            byId.delete(id);
        }
    }

    const rest = fallbackSort(Array.from(byId.values()));
    return [...ordered, ...rest];
};

export const moveWithinColumn = (
    order: KanbanColumnOrder,
    status: number,
    activeId: number,
    overId: number,
    columnTaskIds: number[],
): KanbanColumnOrder => {
    const key = String(status);
    const current = order[key]?.length ? [...order[key]] : [...columnTaskIds];

    // Ensure all current column ids are present (new tasks may be missing from LS)
    for (const id of columnTaskIds) {
        if (!current.includes(id)) current.push(id);
    }

    const oldIndex = current.indexOf(activeId);
    const newIndex = current.indexOf(overId);
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
        return { ...order, [key]: current };
    }

    return { ...order, [key]: arrayMove(current, oldIndex, newIndex) };
};

export const onStatusChanged = (
    order: KanbanColumnOrder,
    taskId: number,
    fromStatus: number,
    toStatus: number,
): KanbanColumnOrder => {
    const next: KanbanColumnOrder = { ...order };
    const fromKey = String(fromStatus);
    const toKey = String(toStatus);

    next[fromKey] = (next[fromKey] || []).filter((id) => id !== taskId);
    const toList = (next[toKey] || []).filter((id) => id !== taskId);
    toList.push(taskId);
    next[toKey] = toList;

    return next;
};
